/* import __COLOCATED_TEMPLATE__ from './list.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import CustomBotListData from 'embercom/objects/operator/configuration/custom-bots/custom-bot-list-data';
import { inject as service } from '@ember/service';
import {
  matchBehaviors,
  objectTypes,
} from 'embercom/models/data/matching-system/matching-constants';
import {
  AI_AGENT_WORKFLOW_TARGET,
  AI_AGENT_WORKFLOW_TRIGGER,
  CHANNEL_ZENDESK_TICKET,
  CHANNEL_ZENDESK_SUNSHINE_CONVERSATION,
  CHANNEL_SALESFORCE_CASE,
  EVERYONE_PREDICATE,
  TRIGGERABLE_BOT_TYPE,
  CHANNEL_WEB,
  CHANNEL_ALL,
  CUSTOM_TASK_TEMPLATES,
} from 'embercom/lib/operator/custom-bots/constants';
import { taskFor } from 'ember-concurrency-ts';
import Ruleset from 'embercom/models/matching-system/ruleset';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { ajaxDelete, get } from 'embercom/lib/ajax';
import { postRequest } from 'embercom/lib/inbox/requests';
import { states } from 'embercom/models/data/matching-system/matching-constants';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import type Template from 'embercom/models/template';
import { task } from 'ember-concurrency-decorators';
import { type BotConfigTarget } from 'embercom/objects/operator/configuration/configuration';
import type BannerDismissalService from 'embercom/services/banner-dismissal-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type ModalService from 'embercom/services/modal-service';

const STANDALONE_COPILOT_BANNER = 'standalone-tasks-list-not-available-in-copilot-banner';
const TASK_PAGE_SIZE = 100;

const DEFAULT_SORT_BY = 'priority';
const DEFAULT_SORT_DIR = 'asc';

interface SortOption {
  sortBy: string;
  sortDir: string;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { sortBy: 'priority', sortDir: 'asc', label: 'standalone.tasks.list.sort.live-first' },
  { sortBy: 'title', sortDir: 'asc', label: 'standalone.tasks.list.sort.name-asc' },
  { sortBy: 'title', sortDir: 'desc', label: 'standalone.tasks.list.sort.name-desc' },
  { sortBy: 'created_at', sortDir: 'desc', label: 'standalone.tasks.list.sort.newest' },
  { sortBy: 'updated_at', sortDir: 'desc', label: 'standalone.tasks.list.sort.last-modified' },
];

export interface Args {
  title: string | null;
  state: string | null;
  sort: string | null;
  sortDir: string | null;
  tags: string | null;
}

interface Signature {
  Args: Args;
}

export default class TasksAndProceduresList extends Component<Signature> {
  listData: CustomBotListData;

  @tracked showContentCreationModal = false;
  @tracked templates: Template[] = [];
  @tracked selectedTemplate: Template | null = null;
  @tracked selectedTemplateContentObject: any = null;
  @tracked objectTypeOverride: number | null = null;
  @tracked selectedTarget: BotConfigTarget | null = null;
  @tracked disableTransitionBack = false;
  @tracked private hasDismissedStandaloneCopilotBanner = true;
  @tracked showAllTasks = false;
  @tracked showAllProcedures = false;
  @tracked connectorHealthStatuses: Record<string, { status: string; has_connectors: boolean }> =
    {};
  @tracked private _availableTags: Array<{ id: string; name: string }> | null = null;

  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intercomEventService: $TSFixMe;
  @service declare outboundHomeService: any;
  @service declare bannerDismissalService: BannerDismissalService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare modalService: ModalService;
  @service declare notificationsService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;

  states = states;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.listData = new CustomBotListData(
      owner,
      this.appService.app.id,
      AI_AGENT_WORKFLOW_TARGET,
      objectTypes.triggerableCustomBot,
    );

    taskFor(this.loadTemplates).perform();
    this.computeStandaloneCopilotBannerVisibility();
  }

  willDestroy() {
    super.willDestroy();
    this.outboundHomeService.resetFilters();
  }

  get isLoadingInitialData() {
    return taskFor(this.listData.setupInitialState).isRunning;
  }

  get procedures(): any[] {
    if (this.appService.app.canUseFinProcedures) {
      let filtered = (this.listData.bots ?? []).filter((bot: any) => {
        let firstContent = bot.contents?.get('firstObject');
        let contentData = firstContent?.contentData;

        if (contentData?.is_procedure !== true) {
          return false;
        }

        if (this.appService.app.canUseFinVoiceProceduresInUI) {
          return true;
        }

        let target_channels: Array<string> = contentData.target_channels;

        return !target_channels?.includes('phone_call');
      });

      if (this.selectedTagIds.length > 0) {
        filtered = filtered.filter((bot: any) =>
          bot.tags?.any((tag: any) => this.selectedTagIds.includes(String(tag.id))),
        );
      }

      return this._sortBots(filtered);
    }

    return [];
  }

  get visibleProcedures(): any[] {
    if (this.showAllProcedures || this.procedures.length <= 10) {
      return this.procedures;
    }
    return this.procedures.slice(0, 10);
  }

  get tasks(): any[] {
    let filtered = (this.listData.bots ?? []).filter((bot: any) => {
      let firstContent = bot.contents?.get('firstObject');
      let contentData = firstContent?.contentData;
      return contentData?.is_procedure !== true;
    });

    if (this.selectedTagIds.length > 0) {
      filtered = filtered.filter((bot: any) =>
        bot.tags?.any((tag: any) => this.selectedTagIds.includes(String(tag.id))),
      );
    }

    return this._sortBots(filtered);
  }

  get visibleTasks(): any[] {
    if (this.showAllTasks || this.tasks.length <= 3) {
      return this.tasks;
    }
    return this.tasks.slice(0, 3);
  }

  @action
  toggleShowAllTasks() {
    this.showAllTasks = !this.showAllTasks;
  }

  @action
  toggleShowAllProcedures() {
    this.showAllProcedures = !this.showAllProcedures;
  }

  get app() {
    return this.appService.app;
  }

  get excludedStates(): string[] {
    return ['scheduled', 'filtered', 'syncing', 'error'];
  }

  get startFromScratchTemplate() {
    return {
      title: 'start-from-scratch',
      icon: 'new',
      id: 'start-from-scratch',
    } as unknown as Template;
  }

  private async computeStandaloneCopilotBannerVisibility() {
    this.hasDismissedStandaloneCopilotBanner =
      await this.bannerDismissalService.hasDismissed(STANDALONE_COPILOT_BANNER);
  }

  get canSeeStandaloneCopilotBanner() {
    return (
      this.appService.app.canUseCopilotOnStandalone &&
      this.finStandaloneService.platform.hasInstalledCopilot &&
      !this.hasDismissedStandaloneCopilotBanner
    );
  }

  get canCreateAndEditProcedures() {
    return this.permissionsService.currentAdminCan('can_create_and_edit_bots');
  }

  get groupList() {
    return [
      {
        items: [
          {
            text: this.intl.t('standalone.tasks.list.let-ai-draft-procedure'),
            onSelectItem: () => this.newProcedureFromAI(),
            icon: 'ai-stars',
          },
          {
            text: this.intl.t('standalone.tasks.list.create-from-scratch'),
            onSelectItem: () => this.newProcedureFromScratch(),
            icon: 'new',
          },
        ],
      },
    ];
  }

  @action
  hideStandaloneCopilotBanner() {
    this.hasDismissedStandaloneCopilotBanner = true;
    this.bannerDismissalService.dismiss(STANDALONE_COPILOT_BANNER);
  }

  @task({ drop: true })
  *loadTemplates() {
    this.templates = yield this.store.findAll('template');
  }

  get taskTemplates() {
    return this.templates.filter((template) => template.objective === CUSTOM_TASK_TEMPLATES.id);
  }

  @action
  selectTemplate(template: Template, isFromEmptyState = false) {
    this.disableTransitionBack = isFromEmptyState;
    this.selectedTemplateContentObject = template.ruleset.rulesetLinks.firstObject.object;
    this.selectedTarget = this.selectedTemplateContentObject.target;
    this.selectedTemplate = template;
  }

  @action
  newTask() {
    this.app.canUseFinTaskTemplate ? this.toggleTemplateModal() : this.createTask();
  }

  @action
  async newProcedureFromScratch() {
    if (!this.canCreateAndEditProcedures) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_create_and_edit_bots',
      );
      return;
    }
    await this.createProcedure();
  }

  private async createProcedure() {
    try {
      let response = await postRequest(`/ember/procedures?app_id=${this.appService.app.id}`);
      let data = await response.json();

      if (data?.id) {
        this.router.transitionTo(this.procedureRoute(), data.id);
      }
    } catch (error) {
      // notifyResponseError automatically handles 403 errors with required_permission
      // by showing the permission modal via permissionsService.checkPermission()
      this.notificationsService.notifyResponseError(error);
    }
  }

  @action
  async newProcedureFromAI() {
    if (!this.canCreateAndEditProcedures) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_create_and_edit_bots',
      );
      return;
    }
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'ai_procedure_generation_start',
      place: 'procedures',
      section: 'fin_ai_agent',
    });

    try {
      let response = await postRequest(`/ember/procedures?app_id=${this.appService.app.id}`);
      let data = await response.json();

      if (data?.id) {
        let route = this.appService.app.isStandaloneApp
          ? 'apps.app.standalone.procedures.procedure.preview'
          : this.procedureRoute();
        this.router.transitionTo(route, data.id, {
          queryParams: { aiDraft: 'true' },
        });
      }
    } catch (error) {
      this.notificationsService.notifyResponseError(error);
    }
  }

  @action
  askAnExpert() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'ask_an_expert_button',
      place: 'standalone_tasks',
    });
  }

  @action
  toggleTemplateModal(selectedTemplateId?: string) {
    this.showContentCreationModal = !this.showContentCreationModal;
    if (!this.showContentCreationModal) {
      this.objectTypeOverride = null;
      this.selectedTemplate = null;
      this.selectedTarget = null;
    } else if (selectedTemplateId) {
      let template = this.templates.find((template) => template.id === selectedTemplateId);
      if (template) {
        this.selectTemplate(template, true);
      }
    }
  }

  @action
  createTaskFromTemplate(
    contentType: string | null = null,
    _deliveryChannel = null,
    _audienceType = null,
    templateId: string | null = null,
  ) {
    this.showContentCreationModal = false;
    let rulesetParams: {
      app_id: string;
      object_type: string | null;
      template_id?: string;
      role_predicate_group?: object;
      object_data?: object;
      match_behavior?: number;
    } = {
      app_id: this.app.id,
      object_type: contentType,
    };

    if (templateId) {
      rulesetParams.template_id = templateId;
    } else {
      rulesetParams.match_behavior = matchBehaviors.transient;
      rulesetParams.role_predicate_group = { predicates: [EVERYONE_PREDICATE] };

      rulesetParams.object_data = {
        target_channels: [CHANNEL_ALL],
        type: TRIGGERABLE_BOT_TYPE,
        trigger_type: AI_AGENT_WORKFLOW_TRIGGER,
      };
    }

    this.router.transitionTo('apps.app.content.new', rulesetParams);
  }

  @action async createTask() {
    let targetChannels = [CHANNEL_WEB];

    try {
      if (this.appService.app.isStandaloneApp) {
        if (this.appService.app.hasStandalonePlatform('zendesk')) {
          targetChannels.pushObjects([
            CHANNEL_ZENDESK_SUNSHINE_CONVERSATION,
            CHANNEL_ZENDESK_TICKET,
          ]);
        }

        if (this.appService.app.hasStandalonePlatform('salesforce')) {
          targetChannels.pushObjects([CHANNEL_SALESFORCE_CASE]);
        }

        let ruleset = await Ruleset.createForType(this.store, {
          app_id: this.appService.app.id,
          match_behavior: matchBehaviors.transient,
          role_predicate_group: { predicates: [EVERYONE_PREDICATE] },
          object_type: objectTypes.triggerableCustomBot,
          object_data: {
            target_channels: targetChannels,
            trigger_type: AI_AGENT_WORKFLOW_TRIGGER,
            type: TRIGGERABLE_BOT_TYPE,
            bot_data: {
              is_procedure: false,
            },
          },
        });
        this.intercomEventService.trackAnalyticsEvent({
          action: 'created',
          object: 'task',
          place: 'standalone_tasks',
        });
        this.router.transitionTo('apps.app.standalone.tasks.workflow', ruleset.id, {
          queryParams: { mode: 'edit' },
        });
      } else {
        targetChannels = [CHANNEL_ALL];

        let ruleset = await Ruleset.createForType(this.store, {
          app_id: this.appService.app.id,
          match_behavior: matchBehaviors.transient,
          role_predicate_group: { predicates: [EVERYONE_PREDICATE] },
          object_type: objectTypes.triggerableCustomBot,
          object_data: {
            target_channels: targetChannels,
            trigger_type: AI_AGENT_WORKFLOW_TRIGGER,
            type: TRIGGERABLE_BOT_TYPE,
            bot_data: {
              is_procedure: false,
            },
          },
        });
        this.intercomEventService.trackAnalyticsEvent({
          action: 'created',
          object: 'task',
          place: 'fin_tasks',
        });
        this.router.transitionTo(this.taskRoute, ruleset.id, {
          queryParams: { mode: 'edit' },
        });
      }
    } catch (error) {
      // notifyResponseError automatically handles 403 errors with required_permission
      // by showing the permission modal via permissionsService.checkPermission()
      this.notificationsService.notifyResponseError(error);
    }
  }

  @action async deleteTaskOrProcess(contentWrapper: {
    contentWrapperId: string;
    title: string;
    contents?: any;
  }) {
    let isProcedure =
      contentWrapper.contents?.get('firstObject')?.contentData?.is_procedure === true;
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('standalone.tasks.list.your-processes.delete-modal.title', {
        processTitle:
          contentWrapper.title ||
          this.intl.t(
            isProcedure
              ? 'standalone.tasks.list.your-processes.untitled-procedure'
              : 'standalone.tasks.list.your-processes.untitled-process',
          ),
      }),
      body: this.intl.t(
        isProcedure
          ? 'standalone.tasks.list.your-processes.delete-modal.procedure-body'
          : 'standalone.tasks.list.your-processes.delete-modal.body',
      ),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('standalone.tasks.list.your-processes.delete-modal.delete'),
      cancelButtonText: this.intl.t('standalone.tasks.list.your-processes.delete-modal.cancel'),
    });

    if (!confirmed) {
      return;
    }

    if (isProcedure && this.appService.app.canUseFeature('team-actions-soft-delete-procedures')) {
      let contentId = contentWrapper.contents?.get('firstObject')?.contentId;
      await ajaxDelete(`/ember/procedures/${contentId}`, {
        app_id: this.appService.app.id,
      });
    } else {
      await ajaxDelete(`/ember/matching_system/rulesets/${contentWrapper.contentWrapperId}`, {
        app_id: this.appService.app.id,
        admin_id: this.appService.app.currentAdmin.id,
      });
    }

    this.listData.bots = this.listData.bots.filter(
      (bot) => bot.id !== contentWrapper.contentWrapperId,
    );

    taskFor(this.listData.reload).perform(TASK_PAGE_SIZE);
  }

  @action
  showMessenger() {
    window.Intercom('showNewMessage');
  }

  @task({ restartable: true })
  *fetchAllTasks() {
    this._setFilterValues();
    this.listData.title = this.args.title;
    yield taskFor(this.listData.setupInitialState).perform(TASK_PAGE_SIZE);

    // Auto-load all remaining pages if there are more items
    while (this.listData.bots.length < this.listData.totalCount) {
      let prevLength = this.listData.bots.length;
      yield taskFor(this.listData.fetchNextPage).perform();
      if (this.listData.bots.length === prevLength) {
        break;
      }
    }

    this.deriveTagsFromBots();
    this.loadConnectorHealthStatuses();
  }

  private async loadConnectorHealthStatuses(): Promise<void> {
    let procedureIds = this.procedures
      .map((p: any) => p.contents?.get('firstObject')?.contentId)
      .filter(Boolean);

    if (procedureIds.length === 0) {
      return;
    }

    try {
      let data = await get('/ember/procedures/connector_health_statuses', {
        app_id: this.appService.app.id,
        procedure_ids: procedureIds,
      });
      this.connectorHealthStatuses = data.statuses || {};
    } catch (e: unknown) {
      console.warn('Failed to load connector health statuses', e);
    }
  }

  @action
  filterContent(_filterProperty: string, value: any) {
    let queryParams = { state: value };
    this.router.transitionTo(this.tasksIndexRoute, { queryParams });
  }

  @action
  resetSearch() {
    this.performSearchTransition({ name: '' });
  }

  @action
  performSearchTransition(item?: { name: string }) {
    let queryParams = {
      title: item && item.name.length > 0 ? item.name : null,
    };
    this.router.transitionTo(this.tasksIndexRoute, { queryParams });
  }

  @action
  resetFilters() {
    this.router.transitionTo(this.tasksIndexRoute, {
      queryParams: {
        title: null,
        state: null,
        tags: null,
      },
    });
  }

  _setFilterValues() {
    this.outboundHomeService.safelySet(
      'selectedStateValue',
      this.args.state !== null ? parseInt(this.args.state, 10) : null,
    );
    // Tag filtering is handled client-side via @tags arg — clear outboundHomeService to prevent stale shared state
    this.outboundHomeService.safelySet('selectedTagValues', null);
  }

  private _sortBots(bots: any[]): any[] {
    let sortBy = this.args.sort ?? DEFAULT_SORT_BY;
    let dirMultiplier = (this.args.sortDir ?? DEFAULT_SORT_DIR) === 'desc' ? -1 : 1;

    return [...bots].sort((a: any, b: any) => {
      if (sortBy === 'title') {
        let aTitle = (a.title ?? '').toLowerCase();
        let bTitle = (b.title ?? '').toLowerCase();
        return aTitle.localeCompare(bTitle) * dirMultiplier;
      } else if (sortBy === 'created_at' || sortBy === 'updated_at') {
        let field = sortBy === 'created_at' ? 'createdAt' : 'updatedAt';
        let aDate = a.contents?.get('firstObject')?.[field]?.getTime() ?? 0;
        let bDate = b.contents?.get('firstObject')?.[field]?.getTime() ?? 0;
        return (aDate - bDate) * dirMultiplier;
      }
      let aIsLive = a.contents?.get('firstObject')?.state === this.states.live ? 0 : 1;
      let bIsLive = b.contents?.get('firstObject')?.state === this.states.live ? 0 : 1;
      return (aIsLive - bIsLive) * dirMultiplier;
    });
  }

  get hasAppliedFilters() {
    return !!(this.args.title != null || this.selectedStateValue != null || this.args.tags != null);
  }

  get selectedStateValue() {
    return this.outboundHomeService.selectedStateValue;
  }

  get currentSortLabel(): string {
    let sortBy = this.args.sort ?? DEFAULT_SORT_BY;
    let sortDir = this.args.sortDir ?? DEFAULT_SORT_DIR;
    let match = SORT_OPTIONS.find((opt) => opt.sortBy === sortBy && opt.sortDir === sortDir);
    return match ? this.intl.t(match.label) : this.intl.t(SORT_OPTIONS[0].label);
  }

  get sortGroupList() {
    let sortBy = this.args.sort ?? DEFAULT_SORT_BY;
    let sortDir = this.args.sortDir ?? DEFAULT_SORT_DIR;
    return [
      {
        items: SORT_OPTIONS.map((opt) => ({
          text: this.intl.t(opt.label),
          isSelected: opt.sortBy === sortBy && opt.sortDir === sortDir,
          onSelectItem: () => this.changeSortOrder(opt.sortBy, opt.sortDir),
        })),
        hasItemsMarkableAsSelected: true,
      },
    ];
  }

  get selectedTagIds(): string[] {
    return this.args.tags ? this.args.tags.split(',') : [];
  }

  get tagFilterLabel(): string {
    let count = this.selectedTagIds.length;
    if (count === 0) {
      return this.intl.t('standalone.tasks.list.filter.tags');
    }
    return this.intl.t('standalone.tasks.list.filter.tags-with-count', { count });
  }

  get availableTags(): Array<{ id: string; name: string }> {
    return this._availableTags ?? [];
  }

  get tagGroupList() {
    if (this.availableTags.length === 0) {
      return [
        {
          items: [
            {
              text: this.intl.t('standalone.tasks.list.filter.no-tags'),
              isDisabled: true,
            },
          ],
        },
      ];
    }
    let groups: any[] = [
      {
        hasItemsMarkableAsSelected: true,
        items: this.availableTags.map((tag) => ({
          text: tag.name,
          isSelected: this.selectedTagIds.includes(String(tag.id)),
          onSelectItem: () => this.toggleTag(String(tag.id)),
        })),
      },
    ];
    if (this.selectedTagIds.length > 0) {
      groups.push({
        items: [
          {
            text: this.intl.t('standalone.tasks.list.filter.clear-tags'),
            onSelectItem: () => this.clearTagFilter(),
          },
        ],
      });
    }
    return groups;
  }

  @action
  changeSortOrder(sortBy: string, sortDir: string) {
    let isDefault = sortBy === DEFAULT_SORT_BY && sortDir === DEFAULT_SORT_DIR;
    this.router.transitionTo(this.tasksIndexRoute, {
      queryParams: {
        sort: isDefault ? null : sortBy,
        sortDir: isDefault ? null : sortDir,
      },
    });
  }

  @action
  toggleTag(tagId: string) {
    let current = this.selectedTagIds;
    let updated = current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId];
    this.router.transitionTo(this.tasksIndexRoute, {
      queryParams: {
        tags: updated.length > 0 ? updated.join(',') : null,
      },
    });
  }

  private clearTagFilter() {
    this.router.transitionTo(this.tasksIndexRoute, {
      queryParams: { tags: null },
    });
  }

  private deriveTagsFromBots() {
    let seen = new Map<string, string>();
    for (let tag of this._availableTags ?? []) {
      seen.set(tag.id, tag.name);
    }
    for (let bot of this.listData.bots ?? []) {
      bot.tags?.forEach((tag: any) => {
        let id = String(tag.id);
        if (!seen.has(id)) {
          seen.set(id, tag.name);
        }
      });
    }
    this._availableTags = Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  @action
  viewTask(taskId: string) {
    if (this.app.canUseFeature('workflow-draft-state')) {
      this.router.transitionTo(this.taskRoute, taskId, {
        queryParams: { mode: 'view' },
      });
    } else {
      this.router.transitionTo(this.taskRoute, taskId);
    }
  }

  @action
  viewTaskForStandalone(taskId: string) {
    if (this.app.canUseFeature('workflow-draft-state')) {
      this.router.transitionTo('apps.app.standalone.tasks.workflow', taskId, {
        queryParams: { mode: 'view' },
      });
    } else {
      this.router.transitionTo('apps.app.standalone.tasks.workflow', taskId);
    }
  }

  get isEcommerceContext(): boolean {
    return this.router.currentRouteName?.includes('ecommerce') ?? false;
  }

  private get tasksIndexRoute(): string {
    if (this.isEcommerceContext) {
      return this.router.currentRouteName?.includes('procedures')
        ? 'apps.app.automation.fin-ai-agent.ecommerce.procedures.index'
        : 'apps.app.automation.fin-ai-agent.ecommerce.tasks.index';
    }
    if (this.appService.app.isStandaloneApp) {
      return 'apps.app.standalone.tasks.index';
    }
    if (this.router.currentRouteName?.includes('procedures')) {
      return 'apps.app.automation.fin-ai-agent.procedures.index';
    }
    return 'apps.app.automation.fin-ai-agent.tasks.index';
  }

  private get taskRoute(): string {
    return this.isEcommerceContext
      ? 'apps.app.automation.fin-ai-agent.ecommerce.tasks.task'
      : 'apps.app.automation.fin-ai-agent.tasks.task';
  }

  procedureRoute(): string {
    if (this.isEcommerceContext) {
      return 'apps.app.automation.fin-ai-agent.ecommerce.procedures.procedure.preview';
    }
    return 'apps.app.automation.fin-ai-agent.procedures.procedure.preview';
  }

  @action
  procedureHref(contentId: string): string {
    let appId = this.appService.app.id;
    return `${this.finAgentBasePath(appId)}/procedures/${contentId}/preview`;
  }

  private finAgentBasePath(appId: string): string {
    return this.isEcommerceContext
      ? `/a/apps/${appId}/automation/fin-ai-agent/ecommerce`
      : `/a/apps/${appId}/automation/fin-ai-agent`;
  }

  @action
  procedureHrefForStandalone(contentId: string): string {
    let appId = this.appService.app.id;
    return `/a/apps/${appId}/standalone/procedures/${contentId}/preview`;
  }

  @action
  viewProcedure(contentId: string, event: MouseEvent) {
    if (event.metaKey || event.ctrlKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.router.transitionTo(this.procedureRoute(), contentId);
  }

  @action
  viewProcedureForStandalone(contentId: string, event: MouseEvent) {
    if (event.metaKey || event.ctrlKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.router.transitionTo('apps.app.standalone.procedures.procedure.preview', contentId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'TasksAndProcedures::List': typeof TasksAndProceduresList;
    'tasks-and-procedures/list': typeof TasksAndProceduresList;
  }
}
